import { useApolloClient, type TypedDocumentNode } from "@apollo/client";
import { useEffect, useRef } from "react";
import { getPollingConfig } from "./jobPollingConfigs";
import { type ResultOf, type VariablesOf } from "@/graphql";
import { getErrorMessage } from "@/lib/utils/errors";
import { selectActiveJobs, selectHasActiveJobs, updateJob as updateJobAction } from "@/store/asyncJobs";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

const POLLING_INTERVAL = 5000;

export const useJobPolling = () => {
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const activeJobsFromSelector = useAppSelector(selectActiveJobs);
  const hasActiveJobs = useAppSelector(selectHasActiveJobs);

  const activeJobsRef = useRef(activeJobsFromSelector);
  const timeoutIdRef = useRef<number | null>(null);

  // Update the ref with the latest jobs on every render
  useEffect(() => {
    activeJobsRef.current = activeJobsFromSelector;
  });

  // Effect to manage the polling loop
  useEffect(() => {
    // Function to perform the actual polling logic
    const pollActiveJobs = async () => {
      const currentActiveJobs = activeJobsRef.current;

      if (currentActiveJobs.length === 0) {
        return; // No active jobs to poll
      }

      // Use Promise.allSettled to poll all jobs concurrently and wait for all to finish/error
      await Promise.allSettled(
        currentActiveJobs.map(async (job) => {
          const config = getPollingConfig<TypedDocumentNode>(job.type);
          if (!config) {
            return; // Skip if no config found for this job type
          }

          try {
            const { data } = await client.query<ResultOf<TypedDocumentNode>, VariablesOf<TypedDocumentNode>>({
              query: config.query,
              variables: config.getQueryVariables(job),
              fetchPolicy: "network-only",
            });

            if (data) {
              const updates = config.getJobStatus(data);
              dispatch(updateJobAction({ id: job.id, ...updates }));
            }
          } catch (error) {
            dispatch(
              updateJobAction({
                id: job.id,
                status: "ERROR",
                error: getErrorMessage(error),
              }),
            );
          }
        }),
      );
    };

    // Function to schedule the next poll
    const schedulePoll = () => {
      // Clear any existing timeout before scheduling a new one
      if (timeoutIdRef.current !== null) {
        window.clearTimeout(timeoutIdRef.current);
      }

      timeoutIdRef.current = window.setTimeout(async () => {
        // Check if still active *before* polling
        if (!activeJobsRef.current.length) {
          return; // Stop if no active jobs anymore
        }
        try {
          await pollActiveJobs();
        } finally {
          // Check if still active *after* polling and schedule next if needed
          if (activeJobsRef.current.length > 0) {
            schedulePoll(); // Schedule the next poll after this one completes
          }
        }
      }, POLLING_INTERVAL);
    };

    // Start polling if there are active jobs
    if (hasActiveJobs) {
      // Run immediately first time, then schedule subsequent polls
      void pollActiveJobs().finally(() => {
        // Only schedule next poll if still active after initial run
        if (activeJobsRef.current.length > 0) {
          schedulePoll();
        }
      });
    }

    // Cleanup function to clear the timeout when the hook unmounts or dependencies change
    return () => {
      if (timeoutIdRef.current !== null) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
    // Rerun effect only when hasActiveJobs changes, or core dependencies change
  }, [hasActiveJobs, client, dispatch]);
};
